import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SendtooLogo from "@/components/shared/SendtooLogo";
import "./Login.css";

function Icon({ type }) {
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  const paths = {
    package: (
      <>
        <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
        <path d="m4.5 7.8 7.5 4.2 7.5-4.2M12 12v9" />
        <path d="m8.2 5.1 7.7 4.3" />
      </>
    ),
    shield: (
      <>
        <path d="M12 3 20 6v5c0 5-3.2 8.4-8 10-4.8-1.6-8-5-8-10V6l8-3Z" />
        <path d="m8.8 12 2.1 2.1 4.5-4.5" />
      </>
    ),
    bolt: <path d="m13 2-8 11h6l-1 9 8-12h-6l1-8Z" />,
    user: (
      <>
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5 20c.8-3.3 3.1-5 7-5s6.2 1.7 7 5" />
      </>
    ),
    lock: (
      <>
        <rect x="5" y="10" width="14" height="10" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    ),
    eye: (
      <>
        <path d="M2.5 12s3.5-5 9.5-5 9.5 5 9.5 5-3.5 5-9.5 5-9.5-5-9.5-5Z" />
        <circle cx="12" cy="12" r="2.2" />
      </>
    ),
    phone: (
      <>
        <rect x="7" y="2.5" width="10" height="19" rx="2" />
        <path d="M10 5h4M11 18.5h2" />
      </>
    ),
    arrow: (
      <>
        <path d="M5 12h13" />
        <path d="m13 7 5 5-5 5" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    map: (
      <>
        <path d="M12 21s-7-6.5-7-11a7 7 0 0 1 14 0c0 4.5-7 11-7 11Z" />
        <circle cx="12" cy="10" r="2.5" />
      </>
    ),
    network: (
      <>
        <circle cx="6" cy="6" r="2.2" />
        <circle cx="18" cy="6" r="2.2" />
        <circle cx="12" cy="18" r="2.2" />
        <path d="M7.7 7.3 10.5 16M16.3 7.3 13.5 16M8.2 6h7.6" />
      </>
    ),
  };

  return <svg {...common}>{paths[type]}</svg>;
}

const BADGES = [
  { icon: "check", label: "Real-Time Tracking" },
  { icon: "map", label: "South India — Own Network" },
  { icon: "network", label: "Pan India — via 3PL" },
  { icon: "bolt", label: "Next-Day Delivery" },
];

export default function Login() {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, []);

  const [mode, setMode] = useState("password"); // password | otp
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({ identifier: "", password: "" });
  const [otpPhone, setOtpPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpStep, setOtpStep] = useState("phone"); // phone | code

  const { login, sendLoginOtp, verifyLoginOtp } = useAuth();
  const navigate = useNavigate();

  const routeByRole = (user) => {
    if (user.role === "admin") navigate("/admin");
    else if (user.role === "agent") navigate("/agent");
    else if (user.role === "staff") navigate("/staff");
    else if (user.role === "partner") navigate("/partner");
    else navigate("/customer");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.identifier, form.password);
      toast.success(`Welcome back, ${user.name}!`);
      routeByRole(user);
    } catch (err) {
      if (err.mustChangePassword) {
        navigate("/auth/reset-password", { state: { resetToken: err.resetToken, identifier: form.identifier } });
      } else if (err.requires2FA) {
        navigate("/auth/verify-2fa", { state: { pendingToken: err.pendingToken } });
      } else {
        toast.error(err.response?.data?.detail || "Login failed. Please check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendLoginOtp(otpPhone.trim());
      toast.success("OTP sent to your phone.");
      setOtpStep("code");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await verifyLoginOtp(otpPhone.trim(), otpCode.trim());
      toast.success(`Welcome back, ${user.name}!`);
      routeByRole(user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onOtpSubmit = otpStep === "phone" ? handleSendOtp : handleVerifyOtp;

  return (
    <main className="login-shell">
      <section className="hero-panel">
        <img className="hero-art" src="/assets/login-hero.png" alt="" aria-hidden="true" />
        <div className="hero-vignette" />
        <div className="hero-overlay-content">
          <div className="hero-logo"><img src="/assets/sendtoo-logo-white.png" alt="Sendtoo" /></div>
          <div className="hero-premium-badge">
            <span className="badge-icon"><Icon type="shield" /></span>
            <span>Premium Logistics. Delivered.</span>
          </div>
          <div className="hero-copy">
            <h2>Moving Possibilities.<br /><span>Delivering Trust.</span></h2>
            <p>End-to-end logistics orchestration platform built for speed, reliability &amp; scale.</p>
            <div className="hero-features">
              <div className="hero-feature">
                <div className="hero-feature-icon"><Icon type="package" /></div>
                <strong>Smart Tracking</strong>
                <span>Real-time visibility</span>
              </div>
              <div className="hero-feature">
                <div className="hero-feature-icon"><Icon type="shield" /></div>
                <strong>Secure Delivery</strong>
                <span>Safety, every time</span>
              </div>
              <div className="hero-feature">
                <div className="hero-feature-icon"><Icon type="bolt" /></div>
                <strong>Lightning Fast</strong>
                <span>Speed that matters</span>
              </div>
            </div>
          </div>
        </div>

        <section className="badges-bar" aria-label="Sendtoo highlights">
          {BADGES.map((b, index) => (
            <span key={b.label} style={{ display: "contents" }}>
              <div className="badge-item">
                <div className="badge-item-icon"><Icon type={b.icon} /></div>
                <span>{b.label}</span>
              </div>
              {index < BADGES.length - 1 && <div className="badge-divider" />}
            </span>
          ))}
        </section>

        <footer className="hero-footer">
          <span className="footer-shield"><Icon type="shield" /></span>
          © {new Date().getFullYear()} Capexlite Logistics Solutions Private Limited. All rights reserved.
        </footer>
      </section>

      <section className="auth-panel">
        <div className="auth-card">
          <div className="mobile-brand">
            <SendtooLogo size="lg" />
          </div>

          <h1>Welcome Back</h1>
          <p className="auth-subtitle">
            {mode === "password" ? "Login to your Sendtoo account" : "Login with a one-time code sent to your phone"}
          </p>

          {mode === "password" && (
            <form onSubmit={handleSubmit}>
              <label htmlFor="identifier">Email or Phone</label>
              <div className="input-wrap">
                <span className="input-icon"><Icon type="user" /></span>
                <input
                  id="identifier"
                  type="text"
                  value={form.identifier}
                  onChange={(e) => setForm({ ...form, identifier: e.target.value })}
                  placeholder="Enter your email or phone number"
                  autoComplete="username"
                  required
                  data-testid="login-identifier"
                />
              </div>

              <label htmlFor="password" className="password-label">Password</label>
              <div className="input-wrap">
                <span className="input-icon"><Icon type="lock" /></span>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <Icon type="eye" />
                </button>
              </div>

              <div className="forgot-row">
                <Link to="/auth/forgot-password" className="text-button">Forgot Password?</Link>
              </div>

              <button className="primary-button" type="submit" disabled={loading} data-testid="login-submit">
                <span>{loading ? "Signing in..." : "Login"}</span>
                {!loading && (
                  <span className="button-arrow"><Icon type="arrow" /></span>
                )}
              </button>
            </form>
          )}

          {mode === "otp" && (
            <form onSubmit={onOtpSubmit}>
              {otpStep === "phone" && (
                <>
                  <label htmlFor="otp-phone">Phone Number</label>
                  <div className="input-wrap">
                    <span className="input-icon"><Icon type="phone" /></span>
                    <input
                      id="otp-phone"
                      type="tel"
                      value={otpPhone}
                      onChange={(e) => setOtpPhone(e.target.value)}
                      placeholder="10-digit mobile number"
                      autoFocus
                      required
                      data-testid="otp-phone"
                    />
                  </div>
                  <div className="otp-hint">We'll send a one-time password to your registered mobile number.</div>
                </>
              )}

              {otpStep === "code" && (
                <>
                  <label htmlFor="otp-code">Enter OTP</label>
                  <div className="input-wrap">
                    <span className="input-icon"><Icon type="check" /></span>
                    <input
                      id="otp-code"
                      type="text"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      placeholder="6-digit code"
                      autoFocus
                      required
                      data-testid="otp-code"
                    />
                  </div>
                  <div className="forgot-row">
                    <button type="button" className="text-button" onClick={() => setOtpStep("phone")}>
                      Change phone number
                    </button>
                  </div>
                </>
              )}

              <button className="primary-button" type="submit" disabled={loading} data-testid="otp-submit">
                <span>{loading ? "Please wait..." : otpStep === "phone" ? "Send OTP" : "Verify & Sign In"}</span>
                {!loading && (
                  <span className="button-arrow"><Icon type="arrow" /></span>
                )}
              </button>
            </form>
          )}

          <div className="or-divider"><span>or</span></div>

          <button
            type="button"
            className="otp-button"
            onClick={() => { setMode(mode === "password" ? "otp" : "password"); setOtpStep("phone"); }}
            data-testid="switch-mode"
          >
            <Icon type="phone" />
            <span>{mode === "otp" ? "Login with Password" : "Login with OTP"}</span>
          </button>

          <p className="signup">
            New to Sendtoo?{" "}
            <Link to="/auth/register" className="text-button signup-link">Create an Account</Link>
          </p>
        </div>
      </section>

    </main>
  );
}
